import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BodyWrapper from './StyledLayout';
import { AnimatedNavBar } from 'components/NavBar';
import {
  selectUser,
  setUserInfo,
  setUserInfoLoading
} from 'redux/slices/userSlice';
import useAdmin from 'hooks/useIsAdmin.hook';
import { CountPendingBonus } from 'helpers/bonusHelper';
import { getInfoUser } from 'services/rest/User/getInfoUser';
import AmbassadorAvatar from 'images/avatars/employee.png';
import {
  selectInvalidToken,
  setInvalidNetworks,
  removeInvalidNetwork,
  dismissInvalidNetwork
} from 'redux/slices/InvalidToken/InvalidTokenSlice';
import { getInvalidNetworks } from 'services/rest/post';
import InvalidTokenBanner from './InvalidTokenBanner';
import { postNetworkToken } from 'services/rest/Network/Network';
import { urlFacebook } from 'constants/setup.constants';
import { TwitterToken } from 'services/rest/Network/twitterToken';
import Swal from 'sweetalert2';
import { Colors } from 'styles/Constants';
import InstagramLogo from 'images/logos/logo-instragram.png';
import { useFetch } from 'hooks/useFetch.hook';
import { userLogout } from 'hooks/useLogout.hook';
import {
  selectCompany,
  setCompanyData
} from 'redux/slices/Company/CompanySlice';
import { getAuthTokenSn } from 'services/rest/Network/getAuthToken';
import { AnnouncementModal } from 'components/Modal/AnnouncementModal';
import {
  ANNOUNCEMENT_MODAL_KEY,
  shouldShowAnnouncementModal
} from 'constants/announcement.constants';
import { dismissAnnouncement } from 'services/rest/Company/DismissAnnouncement';
import { WebinarModal } from 'components/Modal/WebinarModal';
import {
  WEBINAR_MODAL_KEY,
  shouldShowWebinarModal,
  dismissWebinarModal
} from 'constants/webinar.constants';
import { AdvproLaunchModal } from 'components/Modal/AdvproLaunchModal';
import {
  ADVPRO_LAUNCH_MODAL_KEY,
  shouldShowAdvproLaunchModal
} from 'constants/advproLaunch.constants';
import { dismissAdvproLaunch } from 'services/rest/Company/DismissAdvproLaunch';
import { AdvproAmbassadorModal } from 'components/Modal/AdvproAmbassadorModal';
import {
  ADVPRO_AMBASSADOR_MODAL_KEY,
  shouldShowAdvproAmbassadorModal,
  dismissAdvproAmbassadorModal
} from 'constants/advproAmbassadorWebinar.constants';
import { NovedadesModal } from 'components/Modal/NovedadesModal';
import {
  NOVEDADES_MODAL_KEY,
  shouldShowNovedadesModal,
  dismissNovedadesModal
} from 'constants/novedades.constants';
import { recordSpecialModalShown } from 'constants/specialModalFrequency.constants';
import posthog from 'posthog-js';

interface LayoutProps {
  pathname: string;
}

const LINKED_FIELD_BY_NETWORK: Record<string, string> = {
  facebook: 'linkedFacebook',
  twitter: 'linkedTwitter',
  linkedin: 'linkedLinkedin',
  instagram: 'linkedInstagram',
  tiktok: 'linkedTiktok'
};

const Layout: React.FC<LayoutProps> = props => {
  const { pathname, children } = props;
  const { t } = useTranslation(['layout', 'social_network', 'profile']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const invalidTokenState = useSelector(selectInvalidToken);
  const { company } = useSelector(selectCompany);
  const { data, loading } = useFetch(
    getInfoUser(currentUser?.user?.id, currentUser?.token, currentUser?.role)
  );
  const userData = data as any;

  const licenseExpirationDate = userData?.company?.licenseDate;
  const [isModalShown, setIsModalShown] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showWebinar, setShowWebinar] = useState(false);
  const [showAdvproLaunch, setShowAdvproLaunch] = useState(false);
  const [showAdvproAmbassador, setShowAdvproAmbassador] = useState(false);
  const [showNovedades, setShowNovedades] = useState(false);

  // Los Modales Especiales (anuncio, webinars, lanzamiento, novedades) comparten
  // dos reglas de familia (ADV-3347): la empresa del subdominio no está excluida
  // (`excludedCompanies.constants.ts`) y cada modal aparece a lo sumo UNA vez
  // cada 7 días por persona (`specialModalFrequency.constants.ts`). La ventana
  // se abre acá, en el mismo efecto que muestra el modal — nunca al cerrarlo —,
  // así que recargar con el modal abierto tampoco lo trae de vuelta. Por eso
  // cada efecto llama a `recordSpecialModalShown` junto al `setShowX(true)`.
  const specialModalUserId = currentUser?.user?.id;

  // Modal de anuncio "Advocates se renovó": se muestra una vez por login a admins
  // de empresa, dentro de la ventana global de 2 semanas y mientras la company no
  // lo haya descartado (clic en el CTA). El flag de sesión lo arma LoginPage.
  // El kill-switch, la ventana, la exclusión y la frecuencia viven en
  // `shouldShowAnnouncementModal`.
  useEffect(() => {
    if (
      currentUser?.role === 'admin' &&
      shouldShowAnnouncementModal(
        company?.announcementEndDate,
        company?.announcementDismissed,
        specialModalUserId
      ) &&
      sessionStorage.getItem('announcement_modal_pending') === 'true'
    ) {
      sessionStorage.setItem('announcement_modal_pending', 'false');
      recordSpecialModalShown(ANNOUNCEMENT_MODAL_KEY, specialModalUserId);
      setShowAnnouncement(true);
    }
  }, [
    currentUser?.role,
    company?.announcementEndDate,
    company?.announcementDismissed,
    specialModalUserId
  ]);

  // Modal del webinar "El gran reset del Marketing en LATAM" (hasta el 23 de
  // julio de 2026, solo admins). Se evalúa al cargar el dashboard — aplica
  // también a sesiones ya activas, no solo tras login. El descarte se persiste
  // en localStorage (ver webinar.constants.ts).
  useEffect(() => {
    if (
      currentUser?.role === 'admin' &&
      shouldShowWebinarModal(specialModalUserId)
    ) {
      recordSpecialModalShown(WEBINAR_MODAL_KEY, specialModalUserId);
      setShowWebinar(true);
    }
  }, [currentUser?.role, specialModalUserId]);

  // CTA "¡Regístrate Ahora!" del modal del webinar: descarte permanente.
  const handleWebinarDismiss = () => {
    dismissWebinarModal();
    setShowWebinar(false);
  };

  // Cerrar (X) o "Más tarde": cierran la vista. No vuelve hasta que venza la
  // ventana de 7 días, que ya se abrió al mostrarlo.
  const handleWebinarClose = () => {
    setShowWebinar(false);
  };

  // CTA del modal: descarta definitivamente para la company (persistido en DB) y
  // cierra. El botón del modal ya abre el formulario de ClickUp en ventana nueva.
  const handleContactCsm = () => {
    dismissAnnouncement(currentUser?.token);
    dispatch(setCompanyData({ ...company, announcementDismissed: true }));
    setShowAnnouncement(false);
  };

  // Modal de lanzamiento AdvPro (webinar del 5 de agosto, solo admins). Se
  // evalúa al cargar el dashboard — aplica también a sesiones ya activas.
  // `advproLaunchDismissed` undefined cuenta como "no descartado": la company
  // persistida de sesiones previas al deploy no trae el campo y la campaña dura
  // 8 días (el alcance pesa más que el edge de re-mostrar en otro dispositivo).
  // Tracking en PostHog con account_id de primer nivel.
  const advproAccountId = currentUser?.user?.companyId;

  useEffect(() => {
    if (
      !showAdvproLaunch &&
      currentUser?.role === 'admin' &&
      shouldShowAdvproLaunchModal(
        company?.advproLaunchDismissed,
        specialModalUserId
      )
    ) {
      recordSpecialModalShown(ADVPRO_LAUNCH_MODAL_KEY, specialModalUserId);
      setShowAdvproLaunch(true);
      posthog.capture('advpro_launch_modal_viewed', {
        account_id: advproAccountId
      });
    }
  }, [currentUser?.role, company?.advproLaunchDismissed, specialModalUserId]);

  // CTA "Reservar mi lugar gratis": descarte permanente por company (DB). El
  // anchor del modal ya abre el formulario de ClickUp en pestaña nueva.
  const handleAdvproCta = () => {
    posthog.capture('advpro_launch_modal_cta_clicked', {
      account_id: advproAccountId
    });
    dismissAdvproLaunch(currentUser?.token);
    dispatch(setCompanyData({ ...company, advproLaunchDismissed: true }));
    setShowAdvproLaunch(false);
  };

  // "Ahora no": cierra la vista. No vuelve hasta que venza la ventana de 7
  // días, que ya se abrió al mostrarlo.
  const handleAdvproDismiss = () => {
    posthog.capture('advpro_launch_modal_dismissed', {
      account_id: advproAccountId
    });
    setShowAdvproLaunch(false);
  };

  // Cerrar (X): ídem "Ahora no".
  const handleAdvproClose = () => {
    posthog.capture('advpro_launch_modal_closed', {
      account_id: advproAccountId
    });
    setShowAdvproLaunch(false);
  };

  // Play del video embebido.
  const handleAdvproVideoPlay = () => {
    posthog.capture('advpro_launch_modal_video_clicked', {
      account_id: advproAccountId
    });
  };

  // Modal del webinar sobre el rol Embajador (19 de agosto, ADV-3170). Mismo
  // patrón y misma audiencia que el modal del 5 de agosto — lo ven los ADMINS,
  // que son quienes se capacitan en la nueva versión; el webinar habla del rol
  // Embajador, no se lo mostramos a los embajadores.
  //
  // La diferencia con el del 5 de agosto: el descarte permanente vive en
  // localStorage por usuario y no en `companies.advpro_launch_dismissed`, para
  // no pisar el flag de esa campaña (reusarlo haría que descartar un modal
  // descarte el otro). Tracking en PostHog con account_id de primer nivel.
  //
  // Hasta ADV-3347 este modal insistía en cada carga (sin snooze, "para
  // maximizar el alcance"); hoy le rige la ventana de 7 días de la familia.
  useEffect(() => {
    if (
      !showAdvproAmbassador &&
      currentUser?.role === 'admin' &&
      shouldShowAdvproAmbassadorModal(specialModalUserId)
    ) {
      recordSpecialModalShown(ADVPRO_AMBASSADOR_MODAL_KEY, specialModalUserId);
      setShowAdvproAmbassador(true);
      posthog.capture('advpro_ambassador_modal_viewed', {
        account_id: advproAccountId
      });
    }
  }, [currentUser?.role, specialModalUserId]);

  // CTA "Reserva mi lugar": descarte permanente para este admin. El anchor del
  // modal ya abre el formulario de ClickUp en pestaña nueva.
  const handleAdvproAmbassadorCta = () => {
    posthog.capture('advpro_ambassador_modal_cta_clicked', {
      account_id: advproAccountId
    });
    dismissAdvproAmbassadorModal(specialModalUserId);
    setShowAdvproAmbassador(false);
  };

  // "Ahora no": cierra la vista. No vuelve hasta que venza la ventana de 7
  // días, que ya se abrió al mostrarlo.
  const handleAdvproAmbassadorDismiss = () => {
    posthog.capture('advpro_ambassador_modal_dismissed', {
      account_id: advproAccountId
    });
    setShowAdvproAmbassador(false);
  };

  // Cerrar (X o backdrop): ídem "Ahora no".
  const handleAdvproAmbassadorClose = () => {
    posthog.capture('advpro_ambassador_modal_closed', {
      account_id: advproAccountId
    });
    setShowAdvproAmbassador(false);
  };

  // Modal de Novedades de septiembre 2026 (ADV-3243): reporte descargable en
  // Atribución + nuevos puntajes en Recompensas. Mismo patrón y audiencia que
  // el modal del webinar del rol Embajador (admins, ventana de 7 días de la
  // familia). La diferencia: el descarte permanente no va en el CTA sino en el
  // link "No volver a mostrar" — el mockup trae ese link dedicado y deja
  // "Entendido"/"Cerrar" como cierres de la vista. Tracking en PostHog con
  // account_id de primer nivel.
  useEffect(() => {
    if (
      !showNovedades &&
      currentUser?.role === 'admin' &&
      shouldShowNovedadesModal(specialModalUserId)
    ) {
      recordSpecialModalShown(NOVEDADES_MODAL_KEY, specialModalUserId);
      setShowNovedades(true);
      posthog.capture('novedades_modal_viewed', {
        account_id: advproAccountId
      });
    }
  }, [currentUser?.role, specialModalUserId]);

  // "No volver a mostrar": descarte permanente para este admin.
  const handleNovedadesDontShowAgain = () => {
    posthog.capture('novedades_modal_dont_show_again', {
      account_id: advproAccountId
    });
    dismissNovedadesModal(specialModalUserId);
    setShowNovedades(false);
  };

  // "Entendido": cierra la vista. No vuelve hasta que venza la ventana de 7
  // días, que ya se abrió al mostrarlo.
  const handleNovedadesUnderstood = () => {
    posthog.capture('novedades_modal_understood', {
      account_id: advproAccountId
    });
    setShowNovedades(false);
  };

  // Cerrar (X, "Cerrar" o backdrop): ídem "Entendido".
  const handleNovedadesClose = () => {
    posthog.capture('novedades_modal_closed', {
      account_id: advproAccountId
    });
    setShowNovedades(false);
  };

  // "Ver cómo" de una feature: solo tracking, la captura la muestra el modal.
  const handleNovedadesCaptureView = (feature: string) => {
    posthog.capture('novedades_modal_capture_viewed', {
      account_id: advproAccountId,
      feature
    });
  };

  useEffect(() => {
    // No mostrar modal si es admin con isOperations
    const isOperationsAdmin =
      currentUser?.role === 'admin' && userData?.isOperations === true;

    if (
      !loading &&
      !isModalShown &&
      currentUser?.role !== 'ambassador' &&
      !isOperationsAdmin
    ) {
      const today = new Date();
      const licenseRawDate = userData?.company?.licenseDate;
      const licenseExpirationDate = licenseRawDate
        ? new Date(licenseRawDate)
        : null;

      const licenseDate = licenseExpirationDate?.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (
        licenseExpirationDate &&
        !isNaN(licenseExpirationDate.getTime()) &&
        licenseExpirationDate < today
      ) {
        Swal.fire({
          customClass: {
            popup: 'my-popup-class',
            title: 'my-title-class',
            htmlContainer: 'my-html-expired',
            confirmButton: 'my-confirm-class',
            cancelButton: 'my-cancel-class'
          },
          title: 'Licencia expirada',
          html: `Tu contratación de AdvocatesPro finalizó el día <br><strong>${licenseDate}</strong>.<br><br>
               Si quieres poder seguir realizando campañas para aprovechar el poder de tus colaboradores, 
               te invitamos a ponerte en contacto con nosotros vía WhatsApp.`,
          icon: 'warning',
          iconColor: '#F27474',
          showCancelButton: true,
          confirmButtonText: 'Renovar',
          cancelButtonText: 'Cerrar sesión',
          confirmButtonColor: Colors.primary,
          cancelButtonColor: Colors.error,
          showCloseButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(result => {
          if (result.isConfirmed) {
            window.location.href = 'https://sge.st/AdvocatesProRenovacion';
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            userLogout(currentUser, userData, dispatch, history);
          }
        });
        setIsModalShown(true);
      }
    }
  }, [loading, isModalShown, licenseExpirationDate]);

  const postData = async (
    network: string,
    token: string,
    secret: string,
    verifier: string,
    screenName: string
  ) => {
    await postNetworkToken(
      currentUser?.token,
      {
        token: token,
        followers: 1,
        photo: '',
        uid: 1,
        secret: secret,
        verifier: verifier,
        screenName: screenName
      },
      network
    )
      .then(response => {
        dispatch(setUserInfoLoading(true));
        if (response.data) {
          // Instagram se vincula sin salir de la página: se saca del aviso acá.
          dispatch(removeInvalidNetwork(network));
          getInfoUser(
            currentUser?.user?.id,
            currentUser?.token,
            'ambassador'
          ).then(response => {
            dispatch(setUserInfo(response.data));
            dispatch(setUserInfoLoading(false));
          });
        }
      })
      .catch(() => {
        dispatch(setUserInfoLoading(false));
      });
  };

  const conect = (conect: string) => {
    switch (conect) {
      case 'facebook':
        window.location.href = urlFacebook(
          company?.facebookCredentials.apiKey,
          conect
        );
        break;
      case 'twitter':
        TwitterToken(currentUser?.token).then(
          response => (window.location.href = response.data.data)
        );
        break;
      case 'instagram':
        Swal.fire({
          color: Colors.secondaryDarkBlue,
          width: 400,
          imageUrl: InstagramLogo,
          imageWidth: 200,
          imageHeight: 100,
          input: 'text',
          showCloseButton: true,
          inputLabel: t('social_network:UserName'),
          confirmButtonColor: Colors.secondaryPurple
        }).then(result => {
          if (result.value != '') {
            if (result.value != undefined) {
              postData(conect, 'code', '', '', result.value);
            }
          }
        });
        break;
      case 'linkedin':
        getAuthTokenSn(currentUser?.token, 'linkedin').then(
          response => (window.location.href = response.data.data)
        );
        break;
      case 'tiktok':
        getAuthTokenSn(currentUser?.token, 'tiktok').then(
          response => (window.location.href = response.data.data)
        );
        break;
    }
  };

  // Aviso de redes a reconectar: una consulta por carga de página y embajador.
  // El backend incluye las que ya estaban marcadas y confirma en vivo las que
  // vencieron por fecha. Si la consulta falla se reintenta en la próxima vista.
  const isAmbassador = currentUser?.role === 'ambassador';
  const ambassadorId = currentUser?.user?.id;
  const isInvalidTokenChecked =
    isAmbassador && invalidTokenState?.userId === ambassadorId;

  useEffect(() => {
    if (!isAmbassador || isInvalidTokenChecked) return;

    getInvalidNetworks(currentUser?.token)
      .then(networks =>
        dispatch(setInvalidNetworks({ userId: ambassadorId, networks }))
      )
      .catch(() => undefined);
  }, [isAmbassador, isInvalidTokenChecked, ambassadorId]);

  // Mientras la consulta no vuelve (o si falló) se muestra lo que ya se sabe:
  // lo agregado en esta sesión y las redes que el embajador ya tiene marcadas.
  // Solo se oculta si la lista es de otro embajador.
  const invalidTokenOwner = invalidTokenState?.userId;
  const sessionNetworks = invalidTokenState?.invalidNetworks ?? [];
  const flaggedNetworks = Object.keys(LINKED_FIELD_BY_NETWORK).filter(
    network =>
      userData?.[LINKED_FIELD_BY_NETWORK[network]] &&
      userData?.social_network?.networks?.[network]?.invalidToken
  );
  const knownInvalidNetworks = isInvalidTokenChecked
    ? sessionNetworks
    : Array.from(new Set([...sessionNetworks, ...flaggedNetworks]));
  const bannerNetworks =
    isAmbassador &&
    (invalidTokenOwner == null || invalidTokenOwner === ambassadorId)
      ? knownInvalidNetworks.filter(
          network => !(invalidTokenState?.dismissed ?? []).includes(network)
        )
      : [];

  const badgetBonus = useAdmin() ? CountPendingBonus() > 0 : false;

  let links;

  const actions = [
    {
      text: t('layout:settings'),
      onClick: async () => {
        history.replace({ pathname: '/settings' });
      }
    },
    {
      text: t('layout:edit_company'),
      onClick: async () => {
        history.replace({ pathname: '/company-settings' });
      }
    },
    {
      text: t('layout:sign_out'),
      onClick: () => userLogout(currentUser, userData, dispatch, history)
    }
  ];

  const ambassadors = [
    {
      to: '/dashboard',
      icon: 'dashboard',
      text: t('layout:dashboard')
    },
    {
      to: '/creators',
      icon: 'challenges',
      text: t('layout:creators')
    },
    {
      to: '/suggested-posts',
      icon: 'suggested-posts',
      text: t('layout:suggested_posts_ambassador'),
      badget: true,
      badgetLabel: 'NEW'
    },
    {
      to: '/faq',
      icon: 'question',
      text: t('layout:faq')
    },
    {
      to: '/legal',
      icon: 'terms',
      text: t('layout:terms'),
      target: '_blank'
    }
  ];

  const admin = [
    {
      to: '/dashboard',
      icon: 'dashboard',
      text: t('layout:dashboard')
    },
    {
      to: '/admin/campaigns',
      icon: 'campaigns',
      text: t('layout:campaigns')
    },
    {
      to: '/creators',
      icon: 'challenges',
      text: t('layout:creators'),
      target: '',
      badget: badgetBonus
    },
    {
      to: '/suggested-posts',
      icon: 'suggested-posts',
      text: t('layout:suggested_posts_admin'),
      badget: true,
      badgetLabel: t('layout:new') || 'NEW'
    },
    {
      to: '/admin/group',
      icon: 'group',
      text: t('layout:groups')
    },
    {
      to: '/admin/award',
      icon: 'awards',
      text: t('layout:awards')
    },
    {
      to: '/admin/reports',
      icon: 'reports',
      text: t('layout:reports')
    }
  ];

  const superAdmin = [
    {
      to: '/companies',
      icon: 'companies',
      text: t('layout:companies')
    },
    {
      to: '/administrators',
      icon: 'administrators',
      text: t('layout:administrators')
    },
    {
      to: '/logs',
      icon: 'reports',
      text: t('layout:Logs')
    },
    {
      to: '/agents',
      icon: 'reports',
      text: t('layout:Agents')
    }
  ];

  switch (currentUser.role) {
    case 'ambassador':
      links =
        userData?.allowBonus && company?.company_permission?.allowShowBonus
          ? ambassadors
          : ambassadors.filter((option: any) => option.to !== '/creators');
      links =
        !userData?.allowSuggestedPost ||
        !company?.company_permission?.allowShowSuggestedPosts
          ? links.filter((option: any) => option.to !== '/suggested-posts')
          : links;
      actions.splice(1, 1);
      break;
    case 'admin': {
      const hiddenRoutes: Record<string, boolean | undefined> = {
        '/suggested-posts': !company?.company_permission
          ?.allowShowSuggestedPosts,
        '/dashboard': company?.challengeExtraSettings?.hideDashboard,
        '/admin/campaigns': company?.challengeExtraSettings?.hideCampaigns,
        '/admin/award': company?.challengeExtraSettings?.hideAwards,
        '/admin/reports': company?.challengeExtraSettings?.hideReports
      };

      links = admin.filter(option => !hiddenRoutes[option.to]);
      break;
    }
    case 'super':
      links = superAdmin;
      break;
    default:
      links = ambassadors;
      actions.splice(1, 1);
      break;
  }

  const navigationLinks = links.map(link => ({
    ...link,
    active: link.to === pathname
  }));

  let fullName: string;
  if (useAdmin()) {
    fullName = `${userData?.name}`;
  } else if (currentUser.role === 'super') {
    fullName = `${userData?.email}`;
  } else {
    fullName = `${userData?.name} ${userData?.lastName}`;
  }

  const userPhoto = useAdmin() ? company?.logo : userData?.photo;

  const isDashboard = pathname === '/dashboard';

  return (
    <>
      <InvalidTokenBanner
        networks={bannerNetworks}
        onConnect={conect}
        onClose={() =>
          bannerNetworks.forEach(network =>
            dispatch(dismissInvalidNetwork(network))
          )
        }
      />

      <AnimatedNavBar
        blueNavbar={isDashboard}
        navigationShadow={true}
        customScroll={30}
        customWidth={640}
        actions={actions}
        links={navigationLinks}
        userName={fullName}
        userPhoto={userPhoto || AmbassadorAvatar}
        loading={loading}
        licenseExpirationDate={licenseExpirationDate}
      />
      <BodyWrapper showHeader={isDashboard} showLinks={true}>
        {children}
      </BodyWrapper>
      <AnnouncementModal
        open={showAnnouncement}
        onClose={() => setShowAnnouncement(false)}
        onContactCsm={handleContactCsm}
      />
      <WebinarModal
        open={showWebinar}
        onClose={handleWebinarClose}
        onLater={handleWebinarClose}
        onRegister={handleWebinarDismiss}
      />
      <AdvproLaunchModal
        open={showAdvproLaunch}
        onClose={handleAdvproClose}
        onDismiss={handleAdvproDismiss}
        onCta={handleAdvproCta}
        onVideoPlay={handleAdvproVideoPlay}
      />
      <AdvproAmbassadorModal
        open={showAdvproAmbassador}
        onClose={handleAdvproAmbassadorClose}
        onDismiss={handleAdvproAmbassadorDismiss}
        onCta={handleAdvproAmbassadorCta}
      />
      <NovedadesModal
        open={showNovedades}
        onClose={handleNovedadesClose}
        onUnderstood={handleNovedadesUnderstood}
        onDontShowAgain={handleNovedadesDontShowAgain}
        onCaptureView={handleNovedadesCaptureView}
      />
    </>
  );
};

export default Layout;
